import PropTypes from "prop-types";
import React, { useState } from "react";

const Calendar = ({ setSelectedDate, closeModal }) => {
  // Create our own Date object handling since we can't use moment
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day

  const [selectedDay, setSelectedDay] = useState(today);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [viewMode, setViewMode] = useState("days"); // 'days', 'months', 'years'
  const [yearRangeStart, setYearRangeStart] = useState(
    Math.floor(today.getFullYear() / 10) * 10
  );

  const daysInWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  // Array of months
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Helper functions for date manipulation
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // const getMonthName = (date) => {
  //   return months[date.getMonth()];
  // };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isBeforeDay = (date1, date2) => {
    if (date1.getFullYear() < date2.getFullYear()) return true;
    if (date1.getFullYear() > date2.getFullYear()) return false;
    if (date1.getMonth() < date2.getMonth()) return true;
    if (date1.getMonth() > date2.getMonth()) return false;
    return date1.getDate() < date2.getDate();
  };

  // Change month by adding/subtracting months
  const changeMonth = (num) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + num);
    setCurrentMonth(newMonth);
  };

  const changeYear = (num) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(newMonth.getFullYear() + num);
    setCurrentMonth(newMonth);
  };

  const changeYearRange = (num) => {
    setYearRangeStart(yearRangeStart + num * 10);
  };

  const changeDay = (day) => {
    if (!isBeforeDay(day, today)) {
      setSelectedDay(day);
      const formattedDate = formatDate(day);
      setSelectedDate && setSelectedDate(formattedDate);
      handleCancel();
    }
  };

  const handleCancel = () => {
    // setIsCalendarOpen && setIsCalendarOpen(false);
    closeModal && closeModal();
  };

  const handleHeaderClick = () => {
    if (viewMode === "days") {
      setViewMode("months");
    } else if (viewMode === "months") {
      setViewMode("years");
    }
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(monthIndex);
    setCurrentMonth(newDate);
    setViewMode("days");
  };

  const handleYearSelect = (year) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setViewMode("months");
  };

  // Get all days to display in the calendar (including previous/next month days)
  const getCalendarDays = () => {
    const totalDays = [];
    const totalLimit = 42; // 6 rows of 7 days

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Days in current month
    const daysInCurrentMonth = getDaysInMonth(year, month);
    for (let i = 1; i <= daysInCurrentMonth; i++) {
      totalDays.push(new Date(year, month, i));
    }

    // First day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    // Add days from previous month
    if (firstDayOfMonth !== 0) {
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

      for (let i = 0; i < firstDayOfMonth; i++) {
        const day = daysInPrevMonth - i;
        totalDays.unshift(new Date(prevYear, prevMonth, day));
      }
    }

    // Add days from next month
    if (totalDays.length < totalLimit) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const daysToAdd = totalLimit - totalDays.length;

      for (let i = 1; i <= daysToAdd; i++) {
        totalDays.push(new Date(nextYear, nextMonth, i));
      }
    }

    return totalDays;
  };

  const getClassName = (day) => {
    if (isSameDay(day, selectedDay)) {
      return "text-primary-200 rounded border border-primary-200";
    } else if (day.getMonth() !== currentMonth.getMonth()) {
      return "text-gray-300";
    } else if (isBeforeDay(day, today)) {
      return "text-gray-400";
    } else {
      return "";
    }
  };

  const renderCalendarHeader = () => {
    if (viewMode === "days") {
      return (
        <div
          className="text-center font-semibold cursor-pointer"
          onClick={handleHeaderClick}
        >
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
      );
    } else if (viewMode === "months") {
      return (
        <div
          className="text-center font-semibold cursor-pointer"
          onClick={handleHeaderClick}
        >
          {currentMonth.getFullYear()}
        </div>
      );
    } else if (viewMode === "years") {
      return (
        <div className="text-center font-semibold">
          {yearRangeStart}-{yearRangeStart + 9}
        </div>
      );
    }
  };

  const renderNavigation = () => {
    if (viewMode === "days") {
      return (
        <>
          <button
            onClick={() => changeMonth(-12)}
            className="focus:outline-none"
          >
            &lt;&lt;
          </button>
          <button
            onClick={() => changeMonth(-1)}
            className="focus:outline-none"
          >
            &lt;
          </button>

          {renderCalendarHeader()}
          <button onClick={() => changeMonth(1)} className="focus:outline-none">
            &gt;
          </button>

          <button
            onClick={() => changeMonth(12)}
            className="focus:outline-none"
          >
            &gt;&gt;
          </button>
        </>
      );
    } else if (viewMode === "months") {
      return (
        <>
          <button
            onClick={() => changeYear(-10)}
            className="focus:outline-none"
          >
            &lt;&lt;
          </button>
          <button onClick={() => changeYear(-1)} className="focus:outline-none">
            &lt;
          </button>

          {renderCalendarHeader()}
          <button onClick={() => changeYear(1)} className="focus:outline-none">
            &gt;
          </button>
          <button onClick={() => changeYear(10)} className="focus:outline-none">
            &gt;&gt;
          </button>
        </>
      );
    } else if (viewMode === "years") {
      return (
        <>
          <button
            onClick={() => changeYearRange(-10)}
            className="focus:outline-none"
          >
            &lt;&lt;
          </button>
          <button
            onClick={() => changeYearRange(-1)}
            className="focus:outline-none"
          >
            &lt;
          </button>

          {renderCalendarHeader()}

          <button
            onClick={() => changeYearRange(1)}
            className="focus:outline-none"
          >
            &gt;
          </button>
          <button
            onClick={() => changeYearRange(10)}
            className="focus:outline-none"
          >
            &gt;&gt;
          </button>
        </>
      );
    }
  };

  const renderDaysView = () => {
    return (
      <>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {daysInWeek.map((day, index) => (
            <div key={index} className="text-center font-medium py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {getCalendarDays().map((day, index) => (
            <div
              key={index}
              onClick={() => changeDay(day)}
              className={`text-center py-1 cursor-pointer hover:bg-gray-100 hover:text-primary-300  ${getClassName(
                day
              )}`}
            >
              {day.getDate()}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderMonthsView = () => {
    return (
      <div className="grid grid-cols-3 gap-4 p-4">
        {months.map((month, index) => (
          <div
            key={index}
            onClick={() => handleMonthSelect(index)}
            className={`text-center py-3 cursor-pointer hover:bg-gray-100 hover:text-primary-300  rounded ${
              currentMonth.getMonth() === index
                ? "bg-primary-300 text-white"
                : ""
            }`}
          >
            {month}
          </div>
        ))}
      </div>
    );
  };

  const renderYearsView = () => {
    const years = [];
    for (let i = -1; i <= 10; i++) {
      years.push(yearRangeStart + i);
    }

    return (
      <div className="grid grid-cols-3 gap-4 p-4">
        {years.map((year, index) => {
          const isCurrentDecade =
            year >= yearRangeStart && year < yearRangeStart + 10;
          return (
            <div
              key={index}
              onClick={() => isCurrentDecade && handleYearSelect(year)}
              className={`text-center py-3 cursor-pointer hover:bg-gray-100 hover:text-primary-300 rounded ${
                !isCurrentDecade ? "text-gray-300 " : ""
              } ${
                currentMonth.getFullYear() === year
                  ? "bg-primary-300 text-white"
                  : ""
              }`}
            >
              {year}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-96 shadow-lg bg-white rounded-lg">
      {/* Header */}
      <div className="px-4 py-2 flex justify-between items-center bg-gray-50 border-b">
        {renderNavigation()}
        {/* {renderCalendarHeader()} */}
      </div>

      {/* Calendar Content */}
      <div className="p-4">
        {viewMode === "days" && renderDaysView()}
        {viewMode === "months" && renderMonthsView()}
        {viewMode === "years" && renderYearsView()}
      </div>

      {/* Footer */}
      {viewMode === "days" && (
        <div className="p-2 border-t border-secondary-300 text-center">
          <button
            className="text-primary-200 font-medium"
            onClick={() => {
              setSelectedDay(today);
              setCurrentMonth(
                new Date(today.getFullYear(), today.getMonth(), 1)
              );
              const formattedDate = formatDate(today);
              setSelectedDate && setSelectedDate(formattedDate);
              handleCancel();
            }}
          >
            Today
          </button>
        </div>
      )}
    </div>
  );
};

Calendar.propTypes = {
  setIsCalendarOpen: PropTypes.func,
  setSelectedDate: PropTypes.func,
  closeModal: PropTypes.func,
};

export default Calendar;
